import FolderIcon from "@mui/icons-material/Folder";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import React, { Fragment, useState } from "react";

const algorithm = {
  "0578": {
    name: "AES_256_GCM_HKDF_SHA512_COMMIT_KEY_ECDSA_P384",
    algoSuiteDataLen: 32,
  },
  "0478": { name: "AES_256_GCM_HKDF_SHA512_COMMIT_KEY", algoSuiteDataLen: 32 },
};

export default function App() {
  let bytePosition = 0;

  const [mainMessage, setMainMessage] = useState("");
  const [tableRows, setTableRows] = useState([]);

  const createData = (field, value) => {
    return { field, value };
  };

  const appendTableRow = (field, value) => {
    setTableRows((currentRows) => [...currentRows, createData(field, value)]);
  };

  const clearTableRows = () => {
    setTableRows([]);
  };

  const readBytes = (file, byteLength) => {
    return new Promise((resolve) => {
      const endBytePosition = bytePosition + byteLength;

      const reader = new FileReader();
      reader.onload = () => {
        bytePosition = endBytePosition;
        resolve(reader.result);
      };

      reader.readAsArrayBuffer(file.slice(bytePosition, endBytePosition));
    });
  };

  const incrementBytePosition = (byteLength) => {
    bytePosition += byteLength;
  };

  const getHex = (data) => {
    return Array.from(new Uint8Array(data))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
  };

  const readVersion = (file) => {
    return new Promise(async (resolve, reject) => {
      const data = await readBytes(file, 1);

      const view = new DataView(data);
      const version = view.getUint8(0);
      if (version !== 2) {
        reject(`Version must be 2, got ${version} instead`);
        return;
      }

      appendTableRow("Version", version);
      resolve();
    });
  };

  const readAlgorithmId = (file) => {
    return new Promise(async (resolve) => {
      const data = await readBytes(file, 2);
      const algorithmId = getHex(data);

      appendTableRow(
        "Algorithm ID (Hex)",
        `${algorithmId} (${algorithm[algorithmId].name || "Unknown"})`
      );
      resolve(algorithmId);
    });
  };

  const readMessageId = (file) => {
    return new Promise(async (resolve) => {
      const data = await readBytes(file, 32);

      appendTableRow("Message ID (Hex)", getHex(data));
      resolve();
    });
  };

  const readAad = (file) => {
    return new Promise(async (resolve) => {
      const aadLenData = await readBytes(file, 2);
      const aadLenview = new DataView(aadLenData);
      const aadLen = aadLenview.getUint16(0);
      if (aadLen === 0) {
        appendTableRow("AAD Count", 0);
        resolve();
        return;
      }

      const aadCountData = await readBytes(file, 2);
      const aadCountview = new DataView(aadCountData);
      const aadCount = aadCountview.getUint16(0);
      appendTableRow("AAD Count", aadCount);

      for (let i = 0; i < aadCount; i++) {
        const aadKeyLenData = await readBytes(file, 2);
        const aadKeyLenview = new DataView(aadKeyLenData);
        const aadKeyLen = aadKeyLenview.getUint16(0);

        const aadKeyData = await readBytes(file, aadKeyLen);
        const aadKey = new TextDecoder().decode(aadKeyData);

        const aadValueLenData = await readBytes(file, 2);
        const aadValueLenview = new DataView(aadValueLenData);
        const aadValueLen = aadValueLenview.getUint16(0);

        const aadValueData = await readBytes(file, aadValueLen);
        appendTableRow(`AAD[${aadKey}] (Hex)`, getHex(aadValueData));
      }
      resolve();
    });
  };

  const readEncryptedDataKey = (file) => {
    return new Promise(async (resolve) => {
      const edkCountData = await readBytes(file, 2);
      const edkCountview = new DataView(edkCountData);
      const edkCount = edkCountview.getUint16(0);
      appendTableRow("Encrypted Data Key Count", edkCount);

      for (let i = 0; i < edkCount; i++) {
        const keyProviderIdLenData = await readBytes(file, 2);
        const keyProviderIdLenview = new DataView(keyProviderIdLenData);
        const keyProviderIdLen = keyProviderIdLenview.getUint16(0);

        const keyProviderIdData = await readBytes(file, keyProviderIdLen);
        const keyProviderId = new TextDecoder().decode(keyProviderIdData);
        appendTableRow(
          `Encrypted Data Key[${i}] Key Provider ID`,
          keyProviderId
        );

        const keyProviderInfoLenData = await readBytes(file, 2);
        const keyProviderInfoLenview = new DataView(keyProviderInfoLenData);
        const keyProviderInfoLen = keyProviderInfoLenview.getUint16(0);

        let keyProviderInfoData = await readBytes(file, keyProviderInfoLen);
        /**
        The following is only for symmetric encryption where additional bytes is appended to the key_id
        4 bytes of tag len
        4 bytes of iv len
        12 bytes of iv
        Refer: aws_encryption_sdk/internal/formatting/serialize.py L392
        */
        const gcm_tag_iv_data_len = 20; //
        if (
          keyProviderInfoLen > gcm_tag_iv_data_len &&
          getHex(keyProviderInfoData).includes("000000800000000c")
        ) {
          keyProviderInfoData = keyProviderInfoData.slice(
            0,
            keyProviderInfoLen - gcm_tag_iv_data_len
          );
        }

        const keyProviderInfo = new TextDecoder().decode(keyProviderInfoData);
        appendTableRow(
          `Encrypted Data Key[${i}] Key Provider Info`,
          keyProviderInfo
        );

        const encryptedDataKeyLenData = await readBytes(file, 2);
        const encryptedDataKeyLenview = new DataView(encryptedDataKeyLenData);
        const encryptedDataKeyLen = encryptedDataKeyLenview.getUint16(0);
        incrementBytePosition(encryptedDataKeyLen);
      }

      resolve();
    });
  };

  const readContentType = (file) => {
    return new Promise(async (resolve) => {
      const contentTypeData = await readBytes(file, 2);
      const contentTypeView = new DataView(contentTypeData);
      const contentType = contentTypeView.getUint16(0);

      appendTableRow(
        "Content Type",
        contentType === 1 ? "Non-Framed" : "Framed"
      );
      resolve();
    });
  };

  const readFrameLength = (file) => {
    return new Promise(async (resolve) => {
      const frameLengthData = await readBytes(file, 4);
      const frameLengthView = new DataView(frameLengthData);
      const frameLength = frameLengthView.getUint32(0);

      appendTableRow("Frame Length", frameLength);
      resolve();
    });
  };

  const readAlgorithmSuiteData = (file, algorithmId) => {
    return new Promise(async (resolve) => {
      const algorithmSuiteData = await readBytes(file, algorithm[algorithmId].algoSuiteDataLen);
      const algorithmSuiteDataHex = getHex(algorithmSuiteData);

      appendTableRow("Algorithm Suite Data, key commitment (Hex)", algorithmSuiteDataHex);
      resolve();
    });
  };

  const readHeaderAuthentication = (file) => {
    return new Promise(async (resolve) => {
      /**
       * All AES-GCM algorithm suites use 12 bytes of initialization vector (IV) and 16 bytes of authentication tag.
       * https://docs.aws.amazon.com/encryption-sdk/latest/developer-guide/algorithms-reference.html
       */
      const initializationVectorData = await readBytes(file, 12);
      appendTableRow("Header Authentication IV (Hex)", getHex(initializationVectorData));

      const authenticationTagData = await readBytes(file, 16);
      appendTableRow("Header Authentication Tag (Hex)", getHex(authenticationTagData));

      resolve();
    });
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    clearTableRows();

    try {
      await readVersion(file);
      const algorithmId = await readAlgorithmId(file);
      await readMessageId(file);
      await readAad(file);
      await readEncryptedDataKey(file);
      await readContentType(file);
      await readFrameLength(file);
      await readAlgorithmSuiteData(file, algorithmId);
      await readHeaderAuthentication(file);
    } catch (error) {
      clearTableRows();
      setMainMessage("Invalid file: " + error);
      return;
    }

    setMainMessage("Filename: " + file.name);
  };

  return (
    <Container maxWidth="lg">
      <Box textAlign="center" sx={{ my: 4 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          AWS Encryption SDK Message Viewer
        </Typography>
        <Box textAlign="center" sx={{ my: 4 }}>
          <Button
            component="label"
            variant="contained"
            color="inherit"
            startIcon={<FolderIcon />}
            onChange={handleFileSelected}
          >
            Open Encrypted Message
            <input type="file" hidden />
          </Button>
        </Box>
        <Box textAlign="center" sx={{ my: 2 }}>
          <Fragment>{mainMessage}</Fragment>
        </Box>
        <Typography variant="h6" component="h6" gutterBottom>
          Headers
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Field</TableCell>
                <TableCell>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableRows.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell>{row.field}</TableCell>
                  <TableCell
                    style={{
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    {row.value}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Container>
  );
}
